/**
 * T-162-001 — Evidence Safe tier guard (US-162 / F-179, Enterprise-only).
 *
 * Route-level entitlement check: reads the tenant's plan tier from the
 * subscription contract (LP routes/subscription.js GET /api/subscription/current
 * → { success, data: { tier, status, ... } }, tier ids from LP config/tiers.js:
 * freemium | essentials | professional | enterprise) and evaluates access
 * BEFORE rendering an Evidence Safe / Delivery Proof route.
 *
 * Returns an explicit gate response (allowed/denied) — the route wrapper
 * (index.tsx) renders the Enterprise upgrade modal on denial instead of the
 * feature. Fail-closed: if the tier cannot be verified, access is denied.
 *
 * Scope (per dispatch): FE slice only. NO serviceGate mounting (T-167 scope
 * ruling pending) and NO Lambda enforcement (T-162-003, separate track).
 */
import { adminApi } from '../../services/api';
import { isSuperAdminSession } from '../../utils/buildUserFromToken';

export type EvidenceSafeFeature = 'evidence-safe' | 'delivery-proof';

/** Route path → feature name. Delivery Proof lands with its own route later. */
export const EVIDENCE_SAFE_ROUTES: Record<string, EvidenceSafeFeature> = {
    '/texas/evidence': 'evidence-safe',
    '/texas/delivery-proof': 'delivery-proof',
};

export const ENTERPRISE_TIER = 'enterprise';

export interface EvidenceSafeGateResponse {
    allowed: boolean;
    denied: boolean;
    feature: EvidenceSafeFeature;
    tier: string | null;
    reason: string;
}

export function isEnterpriseTier(tier: string | null | undefined): boolean {
    return typeof tier === 'string' && tier.trim().toLowerCase() === ENTERPRISE_TIER;
}

/** Pure evaluator — the T-162-001 gate decision. */
export function evaluateEvidenceSafeGate(
    tier: string | null | undefined,
    feature: EvidenceSafeFeature = 'evidence-safe'
): EvidenceSafeGateResponse {
    const normalized = tier == null || String(tier).trim() === '' ? null : String(tier).trim().toLowerCase();
    const allowed = isEnterpriseTier(normalized);
    return {
        allowed,
        denied: !allowed,
        feature,
        tier: normalized,
        reason: allowed
            ? 'Enterprise tier verified.'
            : `Evidence Safe requires the Enterprise tier — current plan: ${normalized || 'unknown'}.`,
    };
}

/** Read the signed-in tenant's tier (LP contract: data.tier). */
export async function fetchTenantTier(): Promise<string | null> {
    const res = await adminApi.get('api/subscription/current');
    const data = res?.data?.data ?? res?.data ?? {};
    const tier = data.tier ?? data.plan_tier ?? null;
    return tier == null ? null : String(tier);
}

/** Async gate entry point used by the route wrapper. Fail-closed on errors.
 *  Super Admin bypass: staff accounts are unrestricted — plan/tier/subscription
 *  does NOT gate protected features for them (short-circuit BEFORE any fetch). */
export async function checkEvidenceSafeAccess(
    feature: EvidenceSafeFeature = 'evidence-safe'
): Promise<EvidenceSafeGateResponse> {
    if (isSuperAdminSession()) {
        return {
            allowed: true,
            denied: false,
            feature,
            tier: null,
            reason: 'Super Admin — unrestricted access to all features.',
        };
    }
    try {
        const tier = await fetchTenantTier();
        return evaluateEvidenceSafeGate(tier, feature);
    } catch {
        return {
            allowed: false,
            denied: true,
            feature,
            tier: null,
            reason: 'Could not verify your plan tier — access blocked until verification succeeds (fail-closed).',
        };
    }
}
